import { tokenService } from './token-service';
import { isApiError } from '@/lib/api/http-client';
import { getLogger } from '@/lib/logger';
import { useAuthStore } from '@/stores/auth-store';

const logger = getLogger({ scope: 'auth.token-refresh-manager' });

/** Every 30 seconds */
const REFRESH_TOKEN_INTERVAL_MS = 30 * 1000;

/** Buffer before expiry to trigger refresh (60 seconds) */
const REFRESH_BUFFER_SECONDS = 60;

const IS_BROWSER = typeof window !== 'undefined';

/**
 * Token refresh manager for handling automatic token refresh.
 * Uses in-memory expiry tracking instead of localStorage.
 */
class TokenRefreshManager {
  private refreshPromise: Promise<boolean> | null = null;
  private refreshTimer: NodeJS.Timeout | null = null;
  private periodicTimer: NodeJS.Timeout | null = null;
  private isRefreshing = false;
  private expiresAt: number | null = null;
  private beforeUnloadHandler: (() => void) | null = null;

  /**
   * Set the token expiry time based on expiresIn (seconds).
   * If expiresIn is zero or negative the token is already expired — skip
   * scheduling and treat the token as expired immediately.
   */
  setExpiresIn(expiresInSec: number): void {
    if (expiresInSec <= 0) {
      logger.warn(
        'setExpiresIn received a non-positive value; token is already expired, skipping refresh scheduling',
        { attributes: { 'auth.expires_in_seconds': expiresInSec } }
      );
      this.expiresAt = null;
      return;
    }
    this.expiresAt = Date.now() + expiresInSec * 1000;
    this.scheduleNextRefresh(expiresInSec);
  }

  /**
   * Check if the access token is expired
   */
  private isTokenExpired(): boolean {
    if (this.expiresAt === null) return true;
    return Date.now() >= this.expiresAt;
  }

  /**
   * Check if the access token is expiring soon
   */
  private isTokenExpiringSoon(): boolean {
    if (this.expiresAt === null) return true;
    return Date.now() >= this.expiresAt - REFRESH_BUFFER_SECONDS * 1000;
  }

  /**
   * Check if token needs refresh and refresh if necessary
   */
  async checkAndRefreshToken(): Promise<boolean> {
    // If already refreshing, wait for the existing refresh to complete
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    // Check if token is expired or expiring soon
    if (!this.isTokenExpired() && !this.isTokenExpiringSoon()) {
      return true; // Token is still valid
    }

    // Start refresh process
    this.refreshPromise = this.performTokenRefresh();

    try {
      const result = await this.refreshPromise;
      return result;
    } finally {
      this.refreshPromise = null;
    }
  }

  /**
   * Perform the actual token refresh
   */
  private async performTokenRefresh(): Promise<boolean> {
    if (this.isRefreshing) {
      return false;
    }

    this.isRefreshing = true;
    const authStore = useAuthStore.getState();

    try {
      authStore.setLoading(true);
      authStore.setError(null);

      // Call token service to refresh — no params, cookie sent automatically
      const refreshResponse = await tokenService.refreshAuthToken();

      // Update in-memory expiry (user details remain unchanged in store)
      this.expiresAt = Date.now() + refreshResponse.expiresIn * 1000;

      // Schedule next refresh check
      this.scheduleNextRefresh(refreshResponse.expiresIn);

      return true;
    } catch (error) {
      logger.error('Token refresh failed', { error });

      // Clear auth state on refresh failure
      this.handleRefreshFailure(error);
      return false;
    } finally {
      this.isRefreshing = false;
      authStore.setLoading(false);
    }
  }

  /**
   * Handle token refresh failure.
   * Only clears auth (triggering logout) for definitive auth rejections (401/403).
   * Transient errors (network, 5xx, timeout) are left for the periodic timer to retry.
   */
  private handleRefreshFailure(error: unknown): void {
    const isAuthRejection = isApiError(error) && (error.status === 401 || error.status === 403);

    if (!isAuthRejection) {
      logger.warn('Transient refresh failure, will retry on next cycle', { error });
      return;
    }

    const authStore = useAuthStore.getState();

    authStore.clearAuth(true);

    const errorMessage = error instanceof Error ? error.message : 'Token refresh failed';
    authStore.setError(`Authentication expired: ${errorMessage}`);

    this.clearRefreshTimer();
    this.expiresAt = null;
  }

  /**
   * Schedule the next token refresh check
   */
  private scheduleNextRefresh(expiresIn: number): void {
    this.clearRefreshTimer();

    // Schedule refresh 60 seconds before expiration (or halfway through if less than 2 minutes)
    const bufferMs = Math.min(REFRESH_BUFFER_SECONDS * 1000, (expiresIn * 1000) / 2);
    const refreshIn = expiresIn * 1000 - bufferMs;

    if (refreshIn > 0) {
      this.refreshTimer = setTimeout(() => {
        void this.checkAndRefreshToken();
      }, refreshIn);
    }
  }

  /**
   * Clear the refresh timer
   */
  private clearRefreshTimer(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  /**
   * Start automatic token refresh monitoring.
   * Call setExpiresIn() BEFORE this method to set the token expiry.
   */
  startAutoRefresh(): void {
    // Clean up timers only — preserve expiresAt that was set before this call
    this.clearTimersOnly();

    // Set up periodic checks every 30 seconds as a fallback
    this.periodicTimer = setInterval(() => {
      if (!this.isRefreshing && this.isTokenExpiringSoon()) {
        void this.checkAndRefreshToken();
      }
    }, REFRESH_TOKEN_INTERVAL_MS);

    // Clean up on page unload
    if (IS_BROWSER) {
      this.beforeUnloadHandler = () => this.stopAutoRefresh();
      window.addEventListener('beforeunload', this.beforeUnloadHandler);
    }
  }

  /**
   * Stop automatic token refresh and clear all state (used for logout)
   */
  stopAutoRefresh(): void {
    this.clearTimersOnly();
    this.expiresAt = null;
  }

  /**
   * Clear timers without resetting expiresAt (used for restart)
   */
  private clearTimersOnly(): void {
    this.clearRefreshTimer();
    if (this.periodicTimer) {
      clearInterval(this.periodicTimer);
      this.periodicTimer = null;
    }
    this.removeBeforeUnloadListener();
    this.refreshPromise = null;
    this.isRefreshing = false;
  }

  /**
   * Force a token refresh (useful for testing or manual refresh)
   */
  async forceRefresh(): Promise<boolean> {
    // Force a refresh regardless of token expiration state
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = this.performTokenRefresh();
    try {
      const result = await this.refreshPromise;
      return result;
    } finally {
      this.refreshPromise = null;
    }
  }

  /**
   * Get current refresh status
   */
  getRefreshStatus(): { isRefreshing: boolean; hasScheduledRefresh: boolean } {
    return { isRefreshing: this.isRefreshing, hasScheduledRefresh: this.refreshTimer !== null };
  }

  private removeBeforeUnloadListener(): void {
    if (IS_BROWSER && this.beforeUnloadHandler) {
      window.removeEventListener('beforeunload', this.beforeUnloadHandler);
      this.beforeUnloadHandler = null;
    }
  }
}

/**
 * Singleton token refresh manager instance
 */
export const tokenRefreshManager = new TokenRefreshManager();
